import {
  ProposalInfluencer,
  EngagementProposalSocialMediaType,
  ProposalJobDescriptionBreakDownMode
} from '@anytag/graphql';
import { EngagementProposalInfluencersFormValues, EngagementProposalRowData, RowInfluencerData } from './types';

export const formatEngagementProposalData = (
  allInfluencers: readonly ProposalInfluencer[],
  jdMode: ProposalJobDescriptionBreakDownMode,
  isEditMode = false
) =>
  allInfluencers.reduce<{
    tableData: EngagementProposalRowData[];
    formValues: EngagementProposalInfluencersFormValues;
  }>(
    (acc, { accountList, summary, ...influencer }) => {
      const currentIndex = acc.tableData.length;
      const hasSocialAccount = jdMode === ProposalJobDescriptionBreakDownMode.SOCIAL_BREAKDOWN && !!accountList;
      const influencerData: RowInfluencerData = {
        id: influencer.id,
        name: influencer.name,
        status: influencer.status,
        avatar: influencer.avatar,
        ttcmStatus: influencer.ttcmStatus,
        hasOnlyOneTiktokAccount:
          accountList.filter((a) => a.socialMedia === EngagementProposalSocialMediaType.TIKTOK).length === 1
      };
      const editableFields = {
        budget: summary.budget || 0,
        profit: summary.profit || 0,
        netBudget: summary.netBudget || 0,
        influencerCost: summary.influencerCost || 0
      };
      acc.tableData.push({
        index: currentIndex,
        className: 'influencer-row',
        influencer: influencerData,
        isEditable: jdMode === ProposalJobDescriptionBreakDownMode.INFLUENCER_BREAKDOWN && isEditMode,
        stats: {
          ...editableFields,
          addedDate: influencer.addedDate,
          ttcmStatus: influencer.ttcmStatus,
          couponCode: influencer.couponCode,
          engagementRateByViews: summary.engagementViewRate,
          engagementRateByFollowers: summary.engagementFollowerRate,
          engagementProposed: summary.engagementProposedCount,
          engagementPosted: summary.engagementPostedCount,
          followersCount: summary.followersCount,
          ttcmCampaignCode: influencer.ttcmCampaignCode || '-',
          ttcmCampaignInviteLink: !hasSocialAccount ? influencer.ttcmCampaignInviteLink : ''
        }
      });
      acc.formValues[`${currentIndex}-budget`] = editableFields.budget.toString();
      acc.formValues[`${currentIndex}-profit`] = editableFields.profit.toString();
      acc.formValues[`${currentIndex}-netBudget`] = editableFields.netBudget.toString();
      acc.formValues[`${currentIndex}-influencerCost`] = editableFields.influencerCost.toString();

      if (hasSocialAccount) {
        accountList.forEach(
          (
            { summary, addedDate, id: accId, name: accName, socialMedia: accSm, ttcmCampaignInviteLink, ttcmStatus },
            index
          ) => {
            const accountIndex = acc.tableData.length;
            const accountEditableFields = {
              budget: summary.budget || 0,
              profit: summary.profit || 0,
              netBudget: summary.netBudget || 0,
              influencerCost: summary.influencerCost || 0
            };

            acc.tableData.push({
              isEditable: isEditMode,
              className: 'account-row',
              index: accountIndex,
              influencer: influencerData,
              socialAccount: {
                id: accId,
                name: accName,
                socialMedia: accSm,
                lastIndex: index === accountList.length - 1
              },
              stats: {
                ...accountEditableFields,
                addedDate: addedDate,
                ttcmStatus,
                engagementRateByViews: summary.engagementViewRate,
                engagementRateByFollowers: summary.engagementFollowerRate,
                engagementProposed: summary.engagementProposedCount,
                engagementPosted: summary.engagementPostedCount,
                followersCount: summary.followersCount,
                ttcmCampaignInviteLink: ttcmCampaignInviteLink
              }
            });
            acc.formValues[`${accountIndex}-budget`] = accountEditableFields.budget.toString();
            acc.formValues[`${accountIndex}-profit`] = accountEditableFields.profit.toString();
            acc.formValues[`${accountIndex}-netBudget`] = accountEditableFields.netBudget.toString();
            acc.formValues[`${accountIndex}-influencerCost`] = accountEditableFields.influencerCost.toString();
          }
        );
      }

      return acc;
    },
    { tableData: [], formValues: {} }
  );
